import { Cell, Funnel, FunnelChart as RFunnelChart, LabelList, ResponsiveContainer, Tooltip } from 'recharts'
import { useWidgetDensity } from '../grid/WidgetDensityContext'
import { Sparkline } from './Sparkline'
import { ChartAccessibleTable, ChartFrame } from './ChartFrame'
import { ChartPatternDefs, chartFill } from './ChartPatternDefs'

export interface FunnelStage {
  key: string
  label: string
  count: number
}

export interface FunnelChartWrapperProps {
  title: string
  stages: FunnelStage[]
  onStageClick?: (stage: FunnelStage) => void
  height?: number
}

/**
 * AC1 — Cases/filings by status.
 *
 * **Auto-density (IB-311 Slice 1):** `compact` drops the in-chart stage labels (the stages are
 * still distinguishable by colour/pattern + tooltip + the data table); `micro` collapses to a
 * `Sparkline` of stage counts + the largest stage.
 */
export function FunnelChartWrapper({ title, stages, onStageClick, height = 260 }: FunnelChartWrapperProps) {
  const { density } = useWidgetDensity()
  const table = (
    <ChartAccessibleTable
      caption={`${title} — data table`}
      columns={[
        { key: 'label', label: 'Status' },
        { key: 'count', label: 'Count', align: 'end' },
      ]}
      rows={stages.map((s) => ({ label: s.label, count: String(s.count) }))}
    />
  )

  const microFallback =
    stages.length > 0 ? (
      <Sparkline points={stages.map((s) => ({ key: s.key, label: s.label, value: s.count }))} />
    ) : undefined

  return (
    <ChartFrame
      title={title}
      height={height}
      table={table}
      microFallback={microFallback}
      chart={
        <ResponsiveContainer width="100%" height="100%">
          <RFunnelChart>
            <ChartPatternDefs seriesCount={stages.length} />
            <Tooltip />
            <Funnel
              data={stages}
              dataKey="count"
              nameKey="label"
              onClick={(entry) => onStageClick?.(entry as unknown as FunnelStage)}
              cursor={onStageClick ? 'pointer' : undefined}
            >
              {density === 'full' ? <LabelList position="right" dataKey="label" fill="var(--iw-text)" stroke="none" /> : null}
              {stages.map((s, i) => (
                <Cell key={s.key} fill={chartFill(i)} />
              ))}
            </Funnel>
          </RFunnelChart>
        </ResponsiveContainer>
      }
    />
  )
}
